import { useDebouncedValue } from '@mantine/hooks';
import { InfiniteListBase, SortPayload } from 'react-admin';
import { useCosmosQueryClient } from 'providers/clients/CosmosQueryClientProvider';
import SkeletonBox from 'components/generic/SkeletonBox';
import { SearchView } from 'components/Search/SearchView';
import { SearchItemsProvider } from 'components/Search/components/SearchItemsProvider';
import { useSearch } from 'components/Search/SearchContextProvider';
import { SearchViewResults } from 'components/Search/components/SearchAdvance/SearchAdvancedView';
import { SearchAdvancedTopBar } from 'components/Search/components/SearchAdvance/SearchAdvancedTopBar';
import { ChannelsPreSearchContent } from 'components/Search/components/SearchAdvance/components/Syndication/channels';

export const ChannelsSearchView = () => {
  const { queryClient } = useCosmosQueryClient();
  const { validListFilter } = useSearch();

  // Debounce the filter changes to minimize rapid re-queries:
  const [debouncedListFilter] = useDebouncedValue(validListFilter, 400);

  // extract 'sort' and 'dir' from the debounced list filter:
  const {
    sort: debouncedSort,
    dir: debouncedOrder,
    ...debouncedFilter
  } = debouncedListFilter;

  // Show a loading state until we have the basic filter object and a query client
  if (!debouncedListFilter || !validListFilter) {
    return (
      <SkeletonBox
        text="Loading Channels Search..."
        width="100%"
        height="100%"
      />
    );
  }

  return (
    <InfiniteListBase
      resource="wire/channel"
      disableAuthentication={false}
      disableSyncWithLocation={true}
      storeKey={false}
      exporter={false}
      perPage={50}
      sort={
        {
          field: debouncedSort,
          order: debouncedOrder,
        } as SortPayload
      }
      filter={debouncedFilter}
      queryOptions={{
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        onSettled: () => {
          // clear RA default prefetch partial record issues
          queryClient.removeQueries({ queryKey: ['wire/channel', 'getOne'] });
        },
      }}
    >
      <SearchItemsProvider>
        <SearchView
          searchInputs={
            <SearchAdvancedTopBar
              hideSearchBar
              preSearchComponent={<ChannelsPreSearchContent />}
            />
          }
          searchResults={<SearchViewResults />}
        />
      </SearchItemsProvider>
    </InfiniteListBase>
  );
};
